<script lang="ts">
    import Button from '../Button.svelte';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import CustomFieldsRenderer from './CustomFieldsRenderer.svelte';
    import { 
        type FormFieldSettings, 
        type CustomField,
        defaultFormFieldSettings,
        getStatesForCountry
    } from '../../api/formSettings';
    import { checkAddress, type Address } from '../../api/address';
    import { ApiResponseError } from '../../api/common';
    import { checkMobileNumber } from '../../api/users';
    import * as t from '../../translations/ActionForm.ts';

    export let onSubmit: () => void = () => {};
    export let submitLabel: string = "Submit";
    export let isSubmitting: boolean = false;
    
    // Form field settings (controls visibility and validation)
    export let settings: FormFieldSettings = defaultFormFieldSettings;
    
    // Custom fields to render
    export let customFields: CustomField[] = [];
    
    // Whether the user is a returning/remembered user (affects subscription text)
    export let isReturningUser: boolean = false;
    
    // User token for mobile number check (to determine if user previously unsubscribed)
    export let userToken: string | undefined = undefined;
    
    // Association phone number for SMS resubscription message
    export let associationPhoneNumber: string = '';

    // Personal Info Fields
    export let honorific: string = '';
    export let firstName: string = '';
    export let lastName: string = '';
    export let suffix: string = '';
    export let email: string = '';
    export let phone: string = '';
    export let fax: string = '';
    export let mobileNumber: string = '';
    
    // Home Address Fields
    export let street: string = '';
    export let city: string = '';
    export let state: string = '';
    export let zipCode: string = '';
    
    // Business Fields
    export let businessName: string = '';
    export let businessTitle: string = '';
    export let businessStreet: string = '';
    export let businessCity: string = '';
    export let businessState: string = '';
    export let businessZipCode: string = '';
    
    // Options
    export let rememberMe: boolean = true;
    export let subscribe: boolean = false;
    export let textSubscribe: boolean = false;
    
    // Custom field values (keyed by field ID)
    export let customFieldValues: Record<number, string> = {};

    // Validation
    let errors: Record<string, string> = {};
    let touched: Record<string, boolean> = {};
    
    // Address validation state
    let homeAddressSuggestions: Address[] = [];
    let businessAddressSuggestions: Address[] = [];
    let homeAddressValidating = false;
    let businessAddressValidating = false;
    let homeAddressMessage = '';
    let businessAddressMessage = '';
    
    // Multiple cities for same ZIP (like AddressSearch handles)
    let homeAlternateCities: string[] = [];
    let businessAlternateCities: string[] = [];
    
    // Validated addresses (with checksums) - exported for parent to use
    export let validatedHomeAddress: Address | null = null;
    export let validatedBusinessAddress: Address | null = null;
    
    // Mobile number validation state (resubscription warning)
    let mobileNumberChecking = false;
    let isNotMobileNumber = false;
    let requiresStartTextToResubscribe = false;
    let mobileCheckDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    // Get states based on country setting
    $: stateOptions = getStatesForCountry(settings.country).map(s => ({
        value: s,
        label: s || 'Select state'
    }));

    // Common honorifics
    const honorificOptions = ['', 'Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Prof.'];

    function formatAssociationText(text?: string): string {
        if (!text) return '';
        return text.replaceAll('{0}', settings.associationDisplayName || 'this organization');
    }

    function validateField(field: string, value: string): string {
        switch (field) {
            case 'honorific':
                return settings.honorificMandatory && !value.trim() ? t.Required_Validation_Message() : '';
            case 'firstName':
                return settings.firstNameMandatory && !value.trim() ? t.First_Name_Required() : '';
            case 'lastName':
                return settings.lastNameMandatory && !value.trim() ? t.Last_Name_Required() : '';
            case 'email':
                if (!value.trim()) return t.Email_Required();
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t.Email_Invalid();
                return '';
            case 'phone':
                if (settings.phoneNumberMandatory && !value.trim()) return t.Phone_Required();
                // Basic format validation - at least 10 digits
                if (value.trim()) {
                    const digitsOnly = value.replace(/\D/g, '');
                    if (digitsOnly.length < 10) return t.Phone_Invalid();
                }
                return '';
            case 'mobileNumber':
                // Mobile number is required when text subscription is checked
                if (textSubscribe && !value.trim()) return t.Mobile_Required();
                // Basic format validation - at least 10 digits
                if (value.trim()) {
                    const digitsOnly = value.replace(/\D/g, '');
                    if (digitsOnly.length < 10) return t.Mobile_Invalid();
                }
                return '';
            // Home Address fields - required when homeAddressMandatory is true
            case 'street':
                return settings.homeAddressMandatory && !value.trim() ? t.Street_Required() : '';
            case 'city':
                return settings.homeAddressMandatory && !value.trim() ? t.City_Required() : '';
            case 'state':
                return settings.homeAddressMandatory && !value.trim() ? t.State_Required() : '';
            case 'zipCode':
                return settings.homeAddressMandatory && !value.trim() ? t.Zip_Required() : '';
            // Business fields
            case 'businessName':
                return settings.companyNameMandatory && !value.trim() ? t.Company_Required() : '';
            case 'businessTitle':
                return settings.titleMandatory && !value.trim() ? t.Title_Required() : '';
            // Business Address fields - required when businessAddressMandatory is true
            case 'businessStreet':
                return settings.businessAddressMandatory && !value.trim() ? t.Business_Street_Required() : '';
            case 'businessCity':
                return settings.businessAddressMandatory && !value.trim() ? t.Business_City_Required() : '';
            case 'businessState':
                return settings.businessAddressMandatory && !value.trim() ? t.Business_State_Required() : '';
            case 'businessZipCode':
                return settings.businessAddressMandatory && !value.trim() ? t.Business_Zip_Required() : '';
            default:
                return '';
        }
    }

    function validateCustomField(field: CustomField, value: string): string {
        if (field.isMandatoryWhenDisplayed && !value?.trim()) {
            return `${field.displayName} is required`;
        }
        return '';
    }

    function handleBlur(field: string) {
        touched[field] = true;
        errors[field] = validateField(field, getFieldValue(field));
    }

    function handleCustomFieldBlur(field: CustomField) {
        const key = `custom_${field.id}`;
        touched[key] = true;
        errors[key] = validateCustomField(field, customFieldValues[field.id] || '');
    }

    function getFieldValue(field: string): string {
        switch (field) {
            case 'honorific': return honorific;
            case 'firstName': return firstName;
            case 'lastName': return lastName;
            case 'suffix': return suffix;
            case 'email': return email;
            case 'phone': return phone;
            case 'fax': return fax;
            case 'mobileNumber': return mobileNumber;
            // Home address fields
            case 'street': return street;
            case 'city': return city;
            case 'state': return state;
            case 'zipCode': return zipCode;
            // Business fields
            case 'businessName': return businessName;
            case 'businessTitle': return businessTitle;
            case 'businessStreet': return businessStreet;
            case 'businessCity': return businessCity;
            case 'businessState': return businessState;
            case 'businessZipCode': return businessZipCode;
            default: return '';
        }
    }

    function getRequiredFields(): string[] {
        const fields: string[] = [];
        
        // Personal info
        if (settings.honorificVisible && settings.honorificMandatory) fields.push('honorific');
        if (settings.firstNameMandatory) fields.push('firstName');
        if (settings.lastNameMandatory) fields.push('lastName');
        if (settings.emailVisible) fields.push('email'); // Email is always required when shown
        if (settings.phoneVisible && settings.phoneNumberMandatory) fields.push('phone');
        
        // Mobile number required when text subscription is checked
        if (settings.mobileNumberVisible && textSubscribe) fields.push('mobileNumber');
        
        // Home address - all fields required when homeAddressMandatory is true
        if (settings.homeSectionVisible && settings.homeAddressMandatory) {
            fields.push('street', 'city', 'state', 'zipCode');
        }
        
        // Business section
        if (settings.businessSectionVisible && settings.businessNameVisible && settings.companyNameMandatory) fields.push('businessName');
        if (settings.businessSectionVisible && settings.businessTitleVisible && settings.titleMandatory) fields.push('businessTitle');
        
        // Business address - all fields required when businessAddressMandatory is true
        if (settings.businessSectionVisible && settings.businessAddressVisible && settings.businessAddressMandatory) {
            fields.push('businessStreet', 'businessCity', 'businessState', 'businessZipCode');
        }
        
        return fields;
    }

    function isCustomFieldVisible(field: CustomField): boolean {
        if (!settings.customFieldsVisible) return false;

        if (field.displaySection === 'User') return true;
        if (field.displaySection === 'Home') return settings.homeSectionVisible;
        if (field.displaySection === 'Business') return settings.businessSectionVisible;
        if (field.displaySection === 'Other') return true;

        return false;
    }

    // Address validation functions - matches AddressSearch.svelte behavior
    let homeAddressSearchCount = 0;
    let businessAddressSearchCount = 0;
    
    async function validateHomeAddress() {
        if (!zipCode || zipCode.length < 5) return;
        if (!street) return; // Need street address too
        
        homeAddressValidating = true;
        homeAddressMessage = '';
        homeAddressSuggestions = [];
        homeAlternateCities = [];
        homeAddressSearchCount += 1;
        const currentSearch = homeAddressSearchCount;
        
        try {
            const response = await checkAddress(street, zipCode, city, state);
            
            // Only process if no newer search was triggered
            if (currentSearch !== homeAddressSearchCount) return;
            
            validatedHomeAddress = null;
            
            if (response.message) {
                // API returned an error message (address not found, etc.)
                homeAddressMessage = response.message;
                if (response.suggestedZipCode) {
                    homeAddressMessage += ` Did you mean ${response.suggestedZipCode}?`;
                }
            } else if (response.addresses && response.addresses.length === 1) {
                const addr = response.addresses[0];
                // Only consider valid if it has a checksum (like AddressSearch does)
                if (addr.checksum) {
                    // Handle alternate cities (like AddressSearch.svelte)
                    if (addr.alternateCities?.length) {
                        homeAlternateCities = addr.alternateCities.map(c => c.Key);
                    } else if (addr.city) {
                        homeAlternateCities = [addr.city];
                    }
                    selectHomeAddress(addr);
                } else {
                    // API returned address but without checksum - treat as not found
                    homeAddressMessage = t.Address_Not_Found();
                }
            } else if (response.addresses && response.addresses.length > 1) {
                // Multiple matches - show suggestions (only those with checksums)
                const validAddresses = response.addresses.filter(a => a.checksum);
                if (validAddresses.length > 0) {
                    homeAddressSuggestions = validAddresses;
                    homeAddressMessage = t.Address_Multiple_Found();
                } else {
                    homeAddressMessage = t.Address_Not_Found();
                }
            } else {
                homeAddressMessage = t.Address_Not_Found();
            }
        } catch (error) {
            console.warn('Home address validation error:', error);
            if (error instanceof ApiResponseError && error.message) {
                homeAddressMessage = error.message;
            } else if (error instanceof Error && error.message) {
                homeAddressMessage = error.message;
            } else {
                homeAddressMessage = t.Address_Validation_Error();
            }
        } finally {
            homeAddressValidating = false;
        }
    }
    
    async function validateBusinessAddress() {
        if (!businessZipCode || businessZipCode.length < 5) return;
        if (!businessStreet) return; // Need street address too
        
        businessAddressValidating = true;
        businessAddressMessage = '';
        businessAddressSuggestions = [];
        businessAlternateCities = [];
        businessAddressSearchCount += 1;
        const currentSearch = businessAddressSearchCount;
        
        try {
            const response = await checkAddress(businessStreet, businessZipCode, businessCity, businessState);
            
            // Only process if no newer search was triggered
            if (currentSearch !== businessAddressSearchCount) return;
            
            validatedBusinessAddress = null;
            
            if (response.message) {
                // API returned an error message (address not found, etc.)
                businessAddressMessage = response.message;
                if (response.suggestedZipCode) {
                    businessAddressMessage += ` Did you mean ${response.suggestedZipCode}?`;
                }
            } else if (response.addresses && response.addresses.length === 1) {
                const addr = response.addresses[0];
                // Only consider valid if it has a checksum (like AddressSearch does)
                if (addr.checksum) {
                    // Handle alternate cities (like AddressSearch.svelte)
                    if (addr.alternateCities?.length) {
                        businessAlternateCities = addr.alternateCities.map(c => c.Key);
                    } else if (addr.city) {
                        businessAlternateCities = [addr.city];
                    }
                    selectBusinessAddress(addr);
                } else {
                    // API returned address but without checksum - treat as not found
                    businessAddressMessage = t.Address_Not_Found();
                }
            } else if (response.addresses && response.addresses.length > 1) {
                // Multiple matches - show suggestions (only those with checksums)
                const validAddresses = response.addresses.filter(a => a.checksum);
                if (validAddresses.length > 0) {
                    businessAddressSuggestions = validAddresses;
                    businessAddressMessage = t.Address_Multiple_Found();
                } else {
                    businessAddressMessage = t.Address_Not_Found();
                }
            } else {
                businessAddressMessage = t.Address_Not_Found();
            }
        } catch (error) {
            console.warn('Business address validation error:', error);
            if (error instanceof ApiResponseError && error.message) {
                businessAddressMessage = error.message;
            } else if (error instanceof Error && error.message) {
                businessAddressMessage = error.message;
            } else {
                businessAddressMessage = t.Address_Validation_Error();
            }
        } finally {
            businessAddressValidating = false;
        }
    }
    
    function selectHomeAddress(address: Address) {
        street = address.streetAddress || street;
        city = address.city || '';
        state = address.state || '';
        zipCode = address.zipCode || zipCode;
        validatedHomeAddress = address;
        homeAddressSuggestions = [];
        homeAddressMessage = '';
    }
    
    function selectBusinessAddress(address: Address) {
        businessStreet = address.streetAddress || businessStreet;
        businessCity = address.city || '';
        businessState = address.state || '';
        businessZipCode = address.zipCode || businessZipCode;
        validatedBusinessAddress = address;
        businessAddressSuggestions = [];
        businessAddressMessage = '';
    }
    
    // Handle city change when multiple cities available
    // Just update the validated address with the selected city - no need to re-validate
    function handleHomeCityChange() {
        if (validatedHomeAddress) {
            validatedHomeAddress = {
                ...validatedHomeAddress,
                city
            };
        }
    }
    
    function handleBusinessCityChange() {
        if (validatedBusinessAddress) {
            validatedBusinessAddress = {
                ...validatedBusinessAddress,
                city: businessCity
            };
        }
    }
    
    // Debounced address validation (like AddressSearch.svelte)
    let homeAddressTimer: ReturnType<typeof setTimeout>;
    let businessAddressTimer: ReturnType<typeof setTimeout>;
    
    function debounceHomeAddress() {
        // Clear previous validation state when input changes
        validatedHomeAddress = null;
        homeAddressMessage = '';
        homeAddressSuggestions = [];
        homeAlternateCities = [];
        
        if (!street || !zipCode) return;
        
        clearTimeout(homeAddressTimer);
        homeAddressTimer = setTimeout(validateHomeAddress, 1000);
    }
    
    function debounceBusinessAddress() {
        // Clear previous validation state when input changes
        validatedBusinessAddress = null;
        businessAddressMessage = '';
        businessAddressSuggestions = [];
        businessAlternateCities = [];
        
        if (!businessStreet || !businessZipCode) return;
        
        clearTimeout(businessAddressTimer);
        businessAddressTimer = setTimeout(validateBusinessAddress, 1000);
    }
    
    // Handle ZIP code blur to trigger address validation (immediate, no debounce)
    function handleZipBlur() {
        handleBlur('zipCode');
        if (street && zipCode && zipCode.length >= 5) {
            clearTimeout(homeAddressTimer);
            validateHomeAddress();
        }
    }
    
    function handleBusinessZipBlur() {
        handleBlur('businessZipCode');
        if (businessStreet && businessZipCode && businessZipCode.length >= 5) {
            clearTimeout(businessAddressTimer);
            validateBusinessAddress();
        }
    }
    
    // Mobile number validation - checks if number is valid and if user needs to resubscribe via SMS
    async function validateMobileNumber() {
        if (!mobileNumber.trim() || !textSubscribe) {
            // Clear warnings when no mobile or text subscription not checked
            isNotMobileNumber = false;
            requiresStartTextToResubscribe = false;
            return;
        }
        
        mobileNumberChecking = true;
        
        try {
            const response = await checkMobileNumber(userToken, mobileNumber);
            isNotMobileNumber = response.isNotMobileNumber;
            requiresStartTextToResubscribe = response.requiresStartTextMessageToResubscribe;
        } catch (error) {
            console.error('Error checking mobile number:', error);
            // Clear warnings on error - don't block form submission
            isNotMobileNumber = false;
            requiresStartTextToResubscribe = false;
        } finally {
            mobileNumberChecking = false;
        }
    }
    
    // Debounced mobile number validation
    function debounceMobileCheck() {
        // Clear previous warnings when input changes
        isNotMobileNumber = false;
        requiresStartTextToResubscribe = false;
        
        if (!mobileNumber.trim() || !textSubscribe) return;
        
        if (mobileCheckDebounceTimer) {
            clearTimeout(mobileCheckDebounceTimer);
        }
        mobileCheckDebounceTimer = setTimeout(validateMobileNumber, 2000);
    }
    
    // Trigger mobile check when text subscribe is checked
    function handleTextSubscribeChange() {
        if (textSubscribe && !mobileNumber.trim()) {
            // Checkbox checked but no mobile - show error
            handleBlur('mobileNumber'); 
        } else if (!textSubscribe) {
            // Checkbox unchecked - clear mobile error and warnings
            errors.mobileNumber = '';
            errors = errors; // trigger reactivity
            isNotMobileNumber = false;
            requiresStartTextToResubscribe = false;
        } else {
            // Checkbox checked and has mobile - validate it
            validateMobileNumber();
        }
    }

    function handleSubmit() {
        // Validate all required fields
        const requiredFields = getRequiredFields();
        let hasErrors = false;
        let firstErrorField: string | null = null;

        requiredFields.forEach(field => {
            touched[field] = true;
            const error = validateField(field, getFieldValue(field));
            errors[field] = error;
            if (error && !firstErrorField) {
                firstErrorField = field;
                hasErrors = true;
            } else if (error) {
                hasErrors = true;
            }
        });

        // Validate custom fields
        if (settings.customFieldsVisible) {
            customFields.filter(isCustomFieldVisible).forEach(field => {
                if (field.isMandatoryWhenDisplayed) {
                    const key = `custom_${field.id}`;
                    touched[key] = true;
                    const error = validateCustomField(field, customFieldValues[field.id] || '');
                    errors[key] = error;
                    if (error && !firstErrorField) {
                        firstErrorField = key;
                        hasErrors = true;
                    } else if (error) {
                        hasErrors = true;
                    }
                }
            });
        }
        
        // Check if home address is mandatory and validated
        if (settings.homeSectionVisible && settings.homeAddressMandatory && !validatedHomeAddress?.checksum) {
            hasErrors = true;
            homeAddressMessage = homeAddressMessage || t.Address_Invalid();
            if (!firstErrorField) {
                firstErrorField = 'street';
            }
        }
        
        // Check if business address is mandatory and validated
        if (settings.businessSectionVisible && settings.businessAddressVisible && settings.businessAddressMandatory && !validatedBusinessAddress?.checksum) {
            hasErrors = true;
            businessAddressMessage = businessAddressMessage || t.Business_Address_Invalid();
            if (!firstErrorField) {
                firstErrorField = 'businessStreet';
            }
        }

        if (hasErrors && firstErrorField) {
            // Scroll to and focus the first field with an error
            scrollToError(firstErrorField);
        } else if (!hasErrors) {
            onSubmit();
        }
    }
    
    // Scroll to the first error field and focus it
    function scrollToError(fieldId: string) {
        // Map field names to their HTML element IDs
        const elementId = fieldId.startsWith('custom_') ? fieldId : fieldId;
        const element = document.getElementById(elementId);
        
        if (element) {
            // Scroll the element into view with some offset from top
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Focus the element after scrolling (with a small delay for smooth scroll)
            setTimeout(() => {
                element.focus();
            }, 300);
        }
    }

    // Filter custom fields by section
    $: userCustomFields = customFields.filter(f => f.displaySection === 'User');
    $: homeCustomFields = customFields.filter(f => f.displaySection === 'Home');
    $: businessCustomFields = customFields.filter(f => f.displaySection === 'Business');
    $: otherCustomFields = customFields.filter(f => f.displaySection === 'Other');

    // Note: rememberMe, subscribe, and textSubscribe values are initialized in the parent component
    // (SignPage.svelte) based on the remembered user's membership status or defaults from settings.
    // We don't override them here to preserve the correct values for returning users.

    // Show required marker helper
    function showRequired(mandatory: boolean): boolean {
        return mandatory && !settings.hideRequiredMarker;
    }

</script>

<form on:submit|preventDefault={handleSubmit} class="space-y-5" data-testid="action-form">
    <!-- Personal Information Section -->
    <div class="space-y-4" data-testid="personal-info-section">
        <!-- Honorific + Name Row -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {#if settings.honorificVisible}
                <div class="space-y-1.5 sm:col-span-2">
                    <div class="grid grid-cols-4 gap-3">
                        <div class="col-span-1 space-y-1.5" data-testid="honorific-field">
                            <label for="honorific" class="block text-base font-medium text-gray-700">
                                {t.Honorific_Label()} {#if showRequired(settings.honorificMandatory)}<span class="text-red-500">*</span>{/if}
                            </label>
                            <select
                                id="honorific"
                                bind:value={honorific}
                                on:blur={() => handleBlur('honorific')}
                                disabled={!settings.nameEditable}
                                data-testid="honorific-select"
                                class="w-full px-3 py-3 border rounded-lg transition-all duration-200 outline-none bg-white
                                    {touched.honorific && errors.honorific 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                        : 'border-gray-300 input-brand-focus'}"
                            >
                                {#each honorificOptions as opt}
                                    <option value={opt}>{opt || 'Select'}</option>
                                {/each}
                            </select>
                            {#if touched.honorific && errors.honorific}
                                <p class="text-sm text-red-500">{errors.honorific}</p>
                            {/if}
                        </div>
                        
                        <div class="col-span-3 grid grid-cols-2 gap-3">
                            <div class="space-y-1.5" data-testid="first-name-field">
                                <label for="firstName" class="block text-base font-medium text-gray-700">
                                    {t.First_Name_Label()} {#if showRequired(settings.firstNameMandatory)}<span class="text-red-500">*</span>{/if}
                                </label>
                                <input
                                    id="firstName"
                                    type="text"
                                    bind:value={firstName}
                                    on:blur={() => handleBlur('firstName')}
                                    disabled={!settings.nameEditable}
                                    data-testid="first-name-input"
                                    class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                                        {touched.firstName && errors.firstName 
                                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                            : 'border-gray-300 input-brand-focus'}"
                                    placeholder="John"
                                />
                                {#if touched.firstName && errors.firstName}
                                    <p class="text-sm text-red-500" data-testid="first-name-error">{errors.firstName}</p>
                                {/if}
                            </div>
                            <div class="space-y-1.5" data-testid="last-name-field">
                                <label for="lastName" class="block text-base font-medium text-gray-700">
                                    {t.Last_Name_Label()} {#if showRequired(settings.lastNameMandatory)}<span class="text-red-500">*</span>{/if}
                                </label>
                                <input
                                    id="lastName"
                                    type="text"
                                    bind:value={lastName}
                                    on:blur={() => handleBlur('lastName')}
                                    disabled={!settings.nameEditable}
                                    data-testid="last-name-input"
                                    class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                                        {touched.lastName && errors.lastName 
                                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                            : 'border-gray-300 input-brand-focus'}"
                                    placeholder="Smith"
                                />
                                {#if touched.lastName && errors.lastName}
                                    <p class="text-sm text-red-500" data-testid="last-name-error">{errors.lastName}</p>
                                {/if}
                            </div>
                        </div>
                    </div>
                </div>
            {:else}
                <!-- Name Row (no honorific) -->
                <div class="space-y-1.5" data-testid="first-name-field">
                    <label for="firstName" class="block text-base font-medium text-gray-700">
                        {t.First_Name_Label()} {#if showRequired(settings.firstNameMandatory)}<span class="text-red-500">*</span>{/if}
                    </label>
                    <input
                        id="firstName"
                        type="text"
                        bind:value={firstName}
                        on:blur={() => handleBlur('firstName')}
                        disabled={!settings.nameEditable}
                        data-testid="first-name-input"
                        class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                            {touched.firstName && errors.firstName 
                                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                : 'border-gray-300 input-brand-focus'}"
                        placeholder="John"
                    />
                    {#if touched.firstName && errors.firstName}
                        <p class="text-sm text-red-500" data-testid="first-name-error">{errors.firstName}</p>
                    {/if}
                </div>

                <div class="space-y-1.5" data-testid="last-name-field">
                    <label for="lastName" class="block text-base font-medium text-gray-700">
                        {t.Last_Name_Label()} {#if showRequired(settings.lastNameMandatory)}<span class="text-red-500">*</span>{/if}
                    </label>
                    <input
                        id="lastName"
                        type="text"
                        bind:value={lastName}
                        on:blur={() => handleBlur('lastName')}
                        disabled={!settings.nameEditable}
                        data-testid="last-name-input"
                        class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                            {touched.lastName && errors.lastName 
                                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                : 'border-gray-300 input-brand-focus'}"
                        placeholder="Smith"
                    />
                    {#if touched.lastName && errors.lastName}
                        <p class="text-sm text-red-500" data-testid="last-name-error">{errors.lastName}</p>
                    {/if}
                </div>
            {/if}
        </div>

        <!-- Suffix (if visible) -->
        {#if settings.suffixVisible}
            <div class="space-y-1.5 w-1/4" data-testid="suffix-field">
                <label for="suffix" class="block text-base font-medium text-gray-700">
                    {t.Suffix_Label()}
                </label>
                <input
                    id="suffix"
                    type="text"
                    bind:value={suffix}
                    disabled={!settings.nameEditable}
                    data-testid="suffix-input"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus"
                    placeholder="Jr., Sr., etc."
                />
            </div>
        {/if}

        <!-- Email -->
        {#if settings.emailVisible}
            <div class="space-y-1.5" data-testid="email-field">
                <label for="email" class="block text-base font-medium text-gray-700">
                    {t.Email_Label()} <span class="text-red-500">*</span>
                </label>
                <input
                    id="email"
                    type="email"
                    bind:value={email}
                    on:blur={() => handleBlur('email')}
                    disabled={!settings.emailEditable}
                    data-testid="email-input"
                    class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                        {touched.email && errors.email 
                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                            : 'border-gray-300 input-brand-focus'}"
                    placeholder="john.smith@email.com"
                />
                {#if touched.email && errors.email}
                    <p class="text-sm text-red-500" data-testid="email-error">{errors.email}</p>
                {/if}
            </div>
        {/if}

        <!-- Phone Number (stacked vertically) -->
        {#if settings.phoneVisible}
            <div class="space-y-1.5" data-testid="phone-field">
                <label for="phone" class="block text-base font-medium text-gray-700">
                    {t.Phone_Label()} {#if showRequired(settings.phoneNumberMandatory)}<span class="text-red-500">*</span>{/if}
                </label>
                <input
                    id="phone"
                    type="tel"
                    bind:value={phone}
                    on:blur={() => handleBlur('phone')}
                    disabled={!settings.phoneNumberEditable}
                    data-testid="phone-input"
                    class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                        {touched.phone && errors.phone 
                            ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                            : 'border-gray-300 input-brand-focus'}"
                    placeholder="(555) 123-4567"
                />
                {#if touched.phone && errors.phone}
                    <p class="text-sm text-red-500" data-testid="phone-error">{errors.phone}</p>
                {/if}
            </div>
        {/if}

        <!-- Fax Number (stacked vertically) -->
        {#if settings.faxVisible}
            <div class="space-y-1.5" data-testid="fax-field">
                <label for="fax" class="block text-base font-medium text-gray-700">
                    {t.Fax_Label()}
                </label>
                <input
                    id="fax"
                    type="tel"
                    bind:value={fax}
                    disabled={!settings.faxEditable}
                    data-testid="fax-input"
                    class="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus"
                    placeholder="(555) 123-4568"
                />
            </div>
        {/if}

        <!-- Mobile Number Section with Text Subscription -->
        {#if settings.mobileNumberVisible}
            <div class="space-y-3" data-testid="mobile-section">
                <!-- Text Subscription Checkbox (above mobile input, like old form) -->
                {#if settings.showTextSubscribeCheckbox}
                    <div class="flex items-start gap-3" data-testid="text-subscribe-field">
                        <input
                            id="textSubscribe"
                            type="checkbox"
                            bind:checked={textSubscribe}
                            on:change={handleTextSubscribeChange}
                            data-testid="text-subscribe-checkbox"
                            class="w-5 h-5 mt-0.5 checkbox-brand border-gray-300 rounded cursor-pointer"
                        />
                        <label for="textSubscribe" class="text-base text-gray-700 cursor-pointer">
                            {#if settings.textingSubscriptionText}
                                {@html settings.textingSubscriptionText}
                            {:else}
                                Yes, sign me up to receive informational and advocacy text messages
                            {/if}
                        </label>
                    </div>
                    
                    <!-- SMS Disclaimer Message -->
                    <div class="text-xs text-gray-500 leading-relaxed" data-testid="sms-disclaimer">
                        {#if settings.mobileSubscriptionMessage}
                            {@html settings.mobileSubscriptionMessage.replace('{0}', settings.associationDisplayName || 'this organization')}
                        {:else}
                            By providing your phone number, you agree to receive informational and advocacy text messages from {settings.associationDisplayName || 'this organization'} via VoterVoice. Msg & data rates may apply. Message frequency varies. Reply HELP for help. Reply STOP to opt out.
                        {/if}
                        {#if settings.privacyPolicyLink}
                            <a href={settings.privacyPolicyLink} target="_blank" class="link-brand hover:underline" data-testid="privacy-policy-link">{settings.privacyPolicyLinkText || 'Privacy Policy'}</a>
                        {/if}
                        {#if settings.termsAndConditionsLink}
                            {#if settings.privacyPolicyLink}&nbsp;|&nbsp;{/if}
                            <a href={settings.termsAndConditionsLink} target="_blank" class="link-brand hover:underline" data-testid="terms-conditions-link">{settings.termsAndConditionsLinkText || 'Terms & Conditions'}</a>
                        {/if}
                    </div>
                {/if}
                
                <!-- Mobile Number Input -->
                <div class="space-y-1.5" data-testid="mobile-number-field">
                    <label for="mobileNumber" class="block text-base font-medium text-gray-700">
                        {t.Mobile_Number_Label()} {#if textSubscribe}<span class="text-red-500">*</span>{/if}
                    </label>
                    <input
                        id="mobileNumber"
                        type="tel"
                        bind:value={mobileNumber}
                        on:blur={() => handleBlur('mobileNumber')}
                        on:input={debounceMobileCheck}
                        data-testid="mobile-number-input"
                        class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                            {touched.mobileNumber && errors.mobileNumber 
                                ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                : 'border-gray-300 input-brand-focus'}"
                        placeholder="(555) 123-4567"
                    />
                    {#if touched.mobileNumber && errors.mobileNumber}
                        <p class="text-sm text-red-500" data-testid="mobile-number-error">{errors.mobileNumber}</p>
                    {/if}
                    
                    <!-- Not a mobile number warning -->
                    {#if textSubscribe && isNotMobileNumber && !mobileNumberChecking}
                        <p class="text-sm text-red-500" data-testid="not-mobile-warning">{t.Mobile_Not_Mobile()}</p>
                    {/if}
                    
                    <!-- Resubscribe via START text warning -->
                    {#if textSubscribe && requiresStartTextToResubscribe && !mobileNumberChecking}
                        <p class="text-sm text-red-500" data-testid="resubscribe-warning">
                            {t.Resubscribe_Text_Warning(associationPhoneNumber || 'the organization')}
                        </p>
                    {/if}
                    
                    <!-- Checking indicator -->
                    {#if mobileNumberChecking}
                        <p class="text-sm text-gray-500 flex items-center gap-1" data-testid="mobile-checking">
                            <svg class="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Checking number...
                        </p>
                    {/if}
                </div>
            </div>
        {/if}

        <!-- User Section Custom Fields -->
        {#if settings.customFieldsVisible && userCustomFields.length > 0}
            <CustomFieldsRenderer
                fields={userCustomFields}
                {customFieldValues}
                {touched}
                {errors}
                onFieldBlur={handleCustomFieldBlur}
                {showRequired}
            />
        {/if}
    </div>

    <!-- Home Address Section -->
    {#if settings.homeSectionVisible}
        <div class="pt-2" data-testid="home-address-section">
            <h3 class="form-section-heading text-lg font-semibold text-gray-900 mb-4" data-testid="home-address-heading">
                {settings.homeInformationSectionHeading || 'Home Address'}
            </h3>
            
            <div class="space-y-4">
                <!-- Street -->
                <div class="space-y-1.5" data-testid="home-street-field">
                    <label for="street" class="block text-base font-medium text-gray-700">
                        {t.Street_Address_Label()} {#if settings.homeAddressMandatory}<span class="text-red-500">*</span>{/if}
                    </label>
                    <input
                        id="street"
                        type="text"
                        bind:value={street}
                        on:input={debounceHomeAddress}
                        disabled={!settings.homeAddressEditable}
                        data-testid="home-street-input"
                        class="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus"
                        placeholder="123 Main Street"
                    />
                </div>

                <!-- City, State, ZIP Row (2+2+2 keeps State label from wrapping in ~426px forms) -->
                <div class="grid grid-cols-6 gap-3">
                    <!-- City - Only show after validation -->
                    {#if validatedHomeAddress?.checksum}
                        <div class="col-span-2 space-y-1.5 min-w-0">
                            <label for="city" class="block text-base font-medium text-gray-700">
                                {t.City_Label()}
                            </label>
                            {#if homeAlternateCities.length > 1}
                                <!-- Multiple cities for this ZIP - show dropdown -->
                                <select
                                    id="city"
                                    bind:value={city}
                                    on:change={handleHomeCityChange}
                                    class="w-full px-3 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus bg-white"
                                >
                                    {#each homeAlternateCities as altCity}
                                        <option value={altCity}>{altCity}</option>
                                    {/each}
                                </select>
                            {:else}
                                <input
                                    id="city"
                                    type="text"
                                    bind:value={city}
                                    disabled
                                    class="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                                />
                            {/if}
                        </div>

                        <!-- State -->
                        <div class="col-span-2 space-y-1.5 min-w-[4.5rem]">
                            <label for="state" class="block text-base font-medium text-gray-700 whitespace-nowrap">
                                {t.State_Label()}
                            </label>
                            <input
                                id="state"
                                type="text"
                                bind:value={state}
                                disabled
                                class="w-full px-2 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 text-center"
                            />
                        </div>
                    {/if}

                    <!-- ZIP Code -->
                    <div class="{validatedHomeAddress?.checksum ? 'col-span-2' : 'col-span-6'} space-y-1.5 min-w-0" data-testid="home-zip-field">
                        <label for="zipCode" class="block text-base font-medium text-gray-700">
                            {t.Zip_Code_Label()} {#if settings.homeAddressMandatory}<span class="text-red-500">*</span>{/if}
                        </label>
                        <div class="relative {validatedHomeAddress?.checksum ? '' : 'w-32'}">
                            <input
                                id="zipCode"
                                type="text"
                                bind:value={zipCode}
                                on:input={debounceHomeAddress}
                                on:blur={handleZipBlur}
                                disabled={!settings.homeAddressEditable}
                                data-testid="home-zip-input"
                                class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                                    {touched.zipCode && errors.zipCode 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                        : 'border-gray-300 input-brand-focus'}"
                                placeholder="62701"
                                maxlength={settings.enableInternationalAddresses ? 9 : (settings.country === 'CA' ? 7 : 5)}
                            />
                            {#if homeAddressValidating}
                                <div class="absolute right-3 top-1/2 -translate-y-1/2" data-testid="home-address-validating-spinner">
                                    <svg class="animate-spin h-5 w-5 spinner-brand" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            {/if}
                        </div>
                        {#if touched.zipCode && errors.zipCode}
                            <p class="text-sm text-red-500" data-testid="home-zip-error">{errors.zipCode}</p>
                        {/if}
                    </div>
                </div>

                <!-- Address Verified indicator -->
                {#if validatedHomeAddress?.checksum}
                    <div class="flex items-center gap-2 text-sm text-green-600" data-testid="home-address-verified">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                        </svg>
                        <span>{t.Address_Verified()}</span>
                    </div>
                {/if}
                
                <!-- Validation Status (loading/error) -->
                {#if homeAddressValidating}
                    <p class="text-sm text-gray-500" data-testid="home-address-validating">{t.Verifying_Address()}</p>
                {:else if homeAddressMessage}
                    <div class="px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="home-address-error">
                        <p class="text-sm text-red-600 m-0">{homeAddressMessage}</p>
                    </div>
                {/if}
                
                <!-- Address Suggestions (multiple matches) -->
                {#if homeAddressSuggestions.length > 0}
                    <div class="space-y-2" data-testid="home-address-suggestions">
                        <p class="text-sm font-medium text-gray-700">Select your address:</p>
                        <div class="space-y-2 max-h-48 overflow-y-auto">
                            {#each homeAddressSuggestions as suggestion, i}
                                <button
                                    type="button"
                                    on:click={() => selectHomeAddress(suggestion)}
                                    data-testid="home-address-suggestion-{i}"
                                    class="w-full text-left p-3 rounded-lg border border-gray-200 suggestion-brand-hover transition-colors"
                                >
                                    <span class="text-sm text-gray-900">
                                        {suggestion.streetAddress || ''}{suggestion.streetAddress ? ', ' : ''}{suggestion.city}, {suggestion.state} {suggestion.zipCode}
                                    </span>
                                </button>
                            {/each}
                        </div>
                    </div>
                {/if}

                <!-- Home Section Custom Fields -->
                {#if settings.customFieldsVisible && homeCustomFields.length > 0}
                    <div class="pt-2 space-y-4">
                        <CustomFieldsRenderer
                            fields={homeCustomFields}
                            {customFieldValues}
                            {touched}
                            {errors}
                            onFieldBlur={handleCustomFieldBlur}
                            {showRequired}
                        />
                    </div>
                {/if}
            </div>
        </div>
    {/if}

    <!-- Business Section -->
    {#if settings.businessSectionVisible}
        <div class="pt-2" data-testid="business-section">
            <h3 class="form-section-heading text-lg font-semibold text-gray-900 mb-4" data-testid="business-section-heading">
                {settings.businessInformationSectionHeading || t.Business_Section()}
            </h3>
            
            <div class="space-y-4">
                <!-- Business Name & Title -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {#if settings.businessNameVisible}
                        <div class="space-y-1.5" data-testid="business-name-field">
                            <label for="businessName" class="block text-base font-medium text-gray-700">
                                {t.Company_Name_Label()} {#if showRequired(settings.companyNameMandatory)}<span class="text-red-500">*</span>{/if}
                            </label>
                            <input
                                id="businessName"
                                type="text"
                                bind:value={businessName}
                                on:blur={() => handleBlur('businessName')}
                                disabled={!settings.businessNameEditable}
                                data-testid="business-name-input"
                                class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                                    {touched.businessName && errors.businessName 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                        : 'border-gray-300 input-brand-focus'}"
                                placeholder="Acme Corp"
                            />
                            {#if touched.businessName && errors.businessName}
                                <p class="text-sm text-red-500" data-testid="business-name-error">{errors.businessName}</p>
                            {/if}
                        </div>
                    {/if}

                    {#if settings.businessTitleVisible}
                        <div class="space-y-1.5" data-testid="business-title-field">
                            <label for="businessTitle" class="block text-base font-medium text-gray-700">
                                {t.Title_Label()} {#if showRequired(settings.titleMandatory)}<span class="text-red-500">*</span>{/if}
                            </label>
                            <input
                                id="businessTitle"
                                type="text"
                                bind:value={businessTitle}
                                on:blur={() => handleBlur('businessTitle')}
                                disabled={!settings.titleEditable}
                                data-testid="business-title-input"
                                class="w-full px-4 py-3 border rounded-lg transition-all duration-200 outline-none
                                    {touched.businessTitle && errors.businessTitle 
                                        ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200' 
                                        : 'border-gray-300 input-brand-focus'}"
                                placeholder="Manager"
                            />
                            {#if touched.businessTitle && errors.businessTitle}
                                <p class="text-sm text-red-500" data-testid="business-title-error">{errors.businessTitle}</p>
                            {/if}
                        </div>
                    {/if}
                </div>

                <!-- Business Address -->
                {#if settings.businessAddressVisible}
                    <div class="space-y-1.5" data-testid="business-street-field">
                        <label for="businessStreet" class="block text-base font-medium text-gray-700">
                            {t.Street_Address_Label()} {#if settings.businessAddressMandatory}<span class="text-red-500">*</span>{/if}
                        </label>
                        <input
                            id="businessStreet"
                            type="text"
                            bind:value={businessStreet}
                            on:input={debounceBusinessAddress}
                            disabled={!settings.businessAddressEditable}
                            data-testid="business-street-input"
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus"
                            placeholder="456 Business Ave"
                        />
                    </div>

                    <!-- Business City, State, ZIP Row -->
                    <div class="grid grid-cols-6 gap-3">
                        <!-- Business City - Only show after validation -->
                        {#if validatedBusinessAddress?.checksum}
                            <div class="col-span-2 space-y-1.5 min-w-0">
                                <label for="businessCity" class="block text-base font-medium text-gray-700">
                                    {t.City_Label()}
                                </label>
                                {#if businessAlternateCities.length > 1}
                                    <!-- Multiple cities for this ZIP - show dropdown -->
                                    <select
                                        id="businessCity"
                                        bind:value={businessCity}
                                        on:change={handleBusinessCityChange}
                                        class="w-full px-3 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus bg-white"
                                    >
                                        {#each businessAlternateCities as altCity}
                                            <option value={altCity}>{altCity}</option>
                                        {/each}
                                    </select>
                                {:else}
                                    <input
                                        id="businessCity"
                                        type="text"
                                        bind:value={businessCity}
                                        disabled
                                        class="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                                    />
                                {/if}
                            </div>

                            <!-- Business State -->
                            <div class="col-span-2 space-y-1.5 min-w-[4.5rem]">
                                <label for="businessState" class="block text-base font-medium text-gray-700 whitespace-nowrap">
                                    {t.State_Label()}
                                </label>
                                <input
                                    id="businessState"
                                    type="text"
                                    bind:value={businessState}
                                    disabled
                                    class="w-full px-2 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 text-center"
                                />
                            </div>
                        {/if}

                        <!-- Business ZIP Code -->
                        <div class="{validatedBusinessAddress?.checksum ? 'col-span-2' : 'col-span-6'} space-y-1.5 min-w-0" data-testid="business-zip-field">
                            <label for="businessZipCode" class="block text-base font-medium text-gray-700">
                                {t.Zip_Code_Label()} {#if settings.businessAddressMandatory}<span class="text-red-500">*</span>{/if}
                            </label>
                            <div class="relative {validatedBusinessAddress?.checksum ? '' : 'w-32'}">
                                <input
                                    id="businessZipCode"
                                    type="text"
                                    bind:value={businessZipCode}
                                    on:input={debounceBusinessAddress}
                                    on:blur={handleBusinessZipBlur}
                                    disabled={!settings.businessAddressEditable}
                                    data-testid="business-zip-input"
                                    class="w-full px-4 py-3 border border-gray-300 rounded-lg transition-all duration-200 outline-none input-brand-focus"
                                    placeholder="62701"
                                    maxlength={settings.enableInternationalAddresses ? 9 : (settings.country === 'CA' ? 7 : 5)}
                                />
                                {#if businessAddressValidating}
                                    <div class="absolute right-3 top-1/2 -translate-y-1/2" data-testid="business-address-validating-spinner">
                                        <svg class="animate-spin h-5 w-5 spinner-brand" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    </div>

                    <!-- Business Address Verified indicator -->
                    {#if validatedBusinessAddress?.checksum}
                        <div class="flex items-center gap-2 text-sm text-green-600" data-testid="business-address-verified">
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                            </svg>
                            <span>{t.Address_Verified()}</span>
                        </div>
                    {/if}
                    
                    <!-- Business Validation Status (loading/error) -->
                    {#if businessAddressValidating}
                        <p class="text-sm text-gray-500" data-testid="business-address-validating">{t.Verifying_Address()}</p>
                    {:else if businessAddressMessage}
                        <div class="px-3 py-2 rounded-lg bg-red-50 border border-red-200" data-testid="business-address-error">
                            <p class="text-sm text-red-600 m-0">{businessAddressMessage}</p>
                        </div>
                    {/if}
                    
                    <!-- Business Address Suggestions -->
                    {#if businessAddressSuggestions.length > 0}
                        <div class="space-y-2" data-testid="business-address-suggestions">
                            <p class="text-sm font-medium text-gray-700">Select your business address:</p>
                            <div class="space-y-2 max-h-48 overflow-y-auto">
                                {#each businessAddressSuggestions as suggestion, i}
                                    <button
                                        type="button"
                                        on:click={() => selectBusinessAddress(suggestion)}
                                        data-testid="business-address-suggestion-{i}"
                                        class="w-full text-left p-3 rounded-lg border border-gray-200 suggestion-brand-hover transition-colors"
                                    >
                                        <span class="text-sm text-gray-900">
                                            {suggestion.streetAddress || ''}{suggestion.streetAddress ? ', ' : ''}{suggestion.city}, {suggestion.state} {suggestion.zipCode}
                                        </span>
                                    </button>
                                {/each}
                            </div>
                        </div>
                    {/if}
                    
                {/if}

                <!-- Business Section Custom Fields -->
                {#if settings.customFieldsVisible && businessCustomFields.length > 0}
                    <div class="pt-2 space-y-4">
                        <CustomFieldsRenderer
                            fields={businessCustomFields}
                            {customFieldValues}
                            {touched}
                            {errors}
                            onFieldBlur={handleCustomFieldBlur}
                            {showRequired}
                        />
                    </div>
                {/if}
            </div>
        </div>
    {/if}

    <!-- Other Information Section -->
    {#if settings.customFieldsVisible && otherCustomFields.length > 0}
        <div class="pt-2" data-testid="other-information-section">
            <h3 class="form-section-heading text-lg font-semibold text-gray-900 mb-4" data-testid="other-information-heading">
                {settings.otherInformationSectionHeading || 'Other Information'}
            </h3>

            <div class="space-y-4">
                <CustomFieldsRenderer
                    fields={otherCustomFields}
                    {customFieldValues}
                    {touched}
                    {errors}
                    onFieldBlur={handleCustomFieldBlur}
                    {showRequired}
                />
            </div>
        </div>
    {/if}

    <!-- Subscribe Checkbox -->
    {#if settings.showSubscribeCheckbox}
        <div class="flex items-start gap-3 pt-2" data-testid="subscribe-field">
            <input
                id="subscribe"
                type="checkbox"
                bind:checked={subscribe}
                data-testid="subscribe-checkbox"
                class="w-5 h-5 mt-0.5 checkbox-brand border-gray-300 rounded cursor-pointer"
            />
            <label for="subscribe" class="text-base text-gray-700 cursor-pointer">
                {#if isReturningUser && settings.subscriptionTextIdentified}
                    {@html formatAssociationText(settings.subscriptionTextIdentified)}
                {:else if settings.subscriptionText}
                    {@html formatAssociationText(settings.subscriptionText)}
                {:else}
                    Subscribe to updates and newsletters
                {/if}
            </label>
        </div>
    {/if}

    <!-- SMS/Text Subscription is now placed inside the Mobile Number section -->

    <!-- Remember Me -->
    {#if settings.showRememberMeCheckbox}
        <div class="flex items-center gap-3 pt-2" data-testid="remember-me-field">
            <input
                id="rememberMe"
                type="checkbox"
                bind:checked={rememberMe}
                data-testid="remember-me-checkbox"
                class="w-5 h-5 checkbox-brand border-gray-300 rounded cursor-pointer"
            />
            <label for="rememberMe" class="text-base text-gray-700 cursor-pointer">
                Remember me on this device
            </label>
        </div>
    {/if}

    <!-- Submit Button -->
    <div class="pt-4" data-testid="submit-section">
        <Button 
            type="action-center"
            class="w-full !min-h-[52px] text-base font-semibold justify-center"
            disabled={isSubmitting}
            on:click={handleSubmit}
            data-testid="submit-button"
        >
            {#if isSubmitting}
                <div class="flex items-center justify-center gap-2" data-testid="submit-loading">
                    <svg class="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Signing...</span>
                </div>
            {:else}
                <div class="flex items-center justify-center gap-2">
                    <span>{settings.submitButtonText || submitLabel}</span>
                    <CaretRightIcon color="currentColor" />
                </div>
            {/if}
        </Button>
    </div>
</form>

<style>
    /* Use brand/association color for focus states instead of hardcoded teal */
    :global(.input-brand-focus:focus) {
        border-color: var(--vv-action-center-button-color, #0d9488);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--vv-action-center-button-color, #0d9488) 20%, transparent);
        outline: none;
    }
    
    /* Checkbox brand color */
    :global(.checkbox-brand) {
        accent-color: var(--vv-action-center-button-color, #0d9488);
    }
    :global(.checkbox-brand:checked) {
        background-color: var(--vv-action-center-button-color, #0d9488);
        border-color: var(--vv-action-center-button-color, #0d9488);
    }
    :global(.checkbox-brand:focus) {
        box-shadow: none;
        outline: none;
    }

    :global(.radio-brand) {
        accent-color: var(--vv-action-center-button-color, #0d9488);
        box-sizing: border-box;
    }
    :global(.radio-brand:focus) {
        box-shadow: none;
        outline: none;
    }
    
    /* Select brand focus */
    :global(.select-brand-focus:focus) {
        border-color: var(--vv-action-center-button-color, #0d9488);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--vv-action-center-button-color, #0d9488) 20%, transparent);
        outline: none;
    }
    
    /* Link brand color */
    :global(.link-brand) {
        color: var(--vv-action-center-button-color, #0d9488);
    }
    :global(.link-brand:hover) {
        opacity: 0.8;
    }
    
    /* Spinner brand color */
    :global(.spinner-brand) {
        color: var(--vv-action-center-button-color, #0d9488);
    }
    
    /* Address suggestion button hover */
    :global(.suggestion-brand-hover:hover) {
        border-color: var(--vv-action-center-button-color, #0d9488);
        background-color: color-mix(in srgb, var(--vv-action-center-button-color, #0d9488) 10%, white);
    }

    /* Keep form controls visually stable when embedded in pages with legacy/global CSS. */
    :global(form[data-testid="action-form"] h3) {
        font-size: 1rem;
        line-height: 1.5rem;
    }
    :global(form[data-testid="action-form"] h3.form-section-heading) {
        font-size: 1.125rem !important;
        line-height: 1.75rem !important;
        font-weight: 600 !important;
        color: #111827 !important;
    }
    :global(form[data-testid="action-form"] label) {
        font-size: 1rem !important;
        line-height: 1.5rem !important;
        font-weight: 500 !important;
        color: #374151 !important;
    }
    :global(form[data-testid="action-form"] p) {
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
    }
    :global(form[data-testid="action-form"] p.text-gray-500) {
        color: #6b7280 !important;
    }
    :global(form[data-testid="action-form"] p.text-red-500) {
        color: #ef4444 !important;
    }
    :global(form[data-testid="action-form"] input[type="text"]),
    :global(form[data-testid="action-form"] input[type="email"]),
    :global(form[data-testid="action-form"] input[type="tel"]),
    :global(form[data-testid="action-form"] input[type="date"]),
    :global(form[data-testid="action-form"] select) {
        font-size: 1rem;
        line-height: 1.5rem;
        box-sizing: border-box;
        height: 3rem;
        min-height: 3rem;
    }
    :global(form[data-testid="action-form"] input[type="checkbox"]) {
        width: 1.25rem !important;
        height: 1.25rem !important;
        flex: 0 0 auto;
    }
    :global(form[data-testid="action-form"] input[type="radio"]) {
        width: 1rem !important;
        height: 1rem !important;
        flex: 0 0 auto;
    }
</style>
